import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-hot-toast";
import type { ApiResponse, ApiRequestConfig } from "../../types/strict";
import { ApiError, JWTPayload } from "../../types/strict";
import { parseJWT } from "../../utils/cookies";
import {
  getToken,
  getRefreshToken,
  setToken,
  removeToken,
  removeRefreshToken,
} from "@/lib/storage";
import { backToLogin } from "@/helpers";

// Environment configuration
const API_BASE_URL = import.meta.env.VITE_BACKEND_API_BASE_URL;
const AUTH_API_BASE_URL = import.meta.env.VITE_API_AUTH_URL;
const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 30000;

// Extend AxiosRequestConfig to carry our custom flags
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  skipErrorHandling?: boolean;
  _retry?: boolean;
}

// API Client class
class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: API_BASE_URL,
      timeout: Number(API_TIMEOUT),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      withCredentials: false,
    });
  }

  private onRefreshed(newToken: string) {
    this.refreshSubscribers.forEach((cb) => cb(newToken));
    this.refreshSubscribers = [];
  }

  private addRefreshSubscriber(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private setupInterceptors(): void {
    // Request interceptor – attach Bearer token
    this.client.interceptors.request.use(
      (config: ExtendedAxiosRequestConfig) => {
        config.headers = config.headers ?? {};
        config.headers["X-Request-ID"] = this.generateRequestId();

        if (!config.skipAuth) {
          const token = getToken();
          if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
        }

        return config;
      },
      (error: unknown) => Promise.reject(error),
    );

    // Response interceptor – handle errors + refresh on 401
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        return this.handleResponseError(error);
      },
    );
  }

  private async handleResponseError(error: AxiosError): Promise<never> {
    const originalConfig = error.config as ExtendedAxiosRequestConfig;

    // Network error
    if (!error.response) {
      const networkError = new ApiError(
        "Network error. Please check your connection.",
        0,
        "NETWORK_ERROR",
        undefined,
        true,
      );
      if (!originalConfig?.skipErrorHandling) {
        toast.error(networkError.message);
      }
      throw networkError;
    }

    const { status, data } = error.response as { status: number; data: any };

    // 401 handling with token refresh
    if (status === 401) {
      const isLoginRequest = originalConfig?.url?.includes("/auth/login");
      const isRefreshRequest = originalConfig?.url?.includes(
        "/auth/refresh-token",
      );

      if (isLoginRequest || isRefreshRequest) {
        // Don't retry login or refresh requests
        const apiError = new ApiError(
          (data as any)?.message || "Invalid credentials.",
          status,
          "UNAUTHORIZED",
        );
        if (!originalConfig?.skipErrorHandling) {
          toast.error(apiError.message);
        }
        throw apiError;
      }

      if (!originalConfig._retry) {
        if (this.isRefreshing) {
          // Queue until refresh completes
          return new Promise((resolve, reject) => {
            this.addRefreshSubscriber((newToken: string) => {
              if (originalConfig.headers) {
                originalConfig.headers["Authorization"] = `Bearer ${newToken}`;
              }
              resolve(this.client(originalConfig) as any);
            });
          }) as any;
        }

        originalConfig._retry = true;
        this.isRefreshing = true;

        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          this.isRefreshing = false;
          backToLogin();
          throw new ApiError(
            "Session expired. Please login again.",
            401,
            "UNAUTHORIZED",
          );
        }

        try {
          const refreshResponse = await this.client.post(
            `${AUTH_API_BASE_URL}/auth/refresh-token`,
            { refreshToken },
            { skipAuth: true } as any,
          );
          const newAccessToken = (refreshResponse.data as any)?.attributes
            ?.tokens?.accessToken;
          if (!newAccessToken)
            throw new Error("No access token in refresh response");

          setToken(newAccessToken);
          this.isRefreshing = false;
          this.onRefreshed(newAccessToken);

          if (originalConfig.headers) {
            originalConfig.headers["Authorization"] =
              `Bearer ${newAccessToken}`;
          }
          return this.client(originalConfig) as any;
        } catch {
          this.isRefreshing = false;
          this.refreshSubscribers = [];
          removeToken();
          removeRefreshToken();
          backToLogin();
          throw new ApiError(
            "Session expired. Please login again.",
            401,
            "UNAUTHORIZED",
          );
        }
      }

      backToLogin();
      throw new ApiError(
        "Session expired. Please login again.",
        401,
        "UNAUTHORIZED",
      );
    }

    let apiError: ApiError;

    switch (status) {
      case 403:
        apiError = new ApiError(
          data?.message ??
            "Access denied. You don't have permission to perform this action.",
          status,
          "FORBIDDEN",
        );
        break;

      case 404:
        apiError = new ApiError(
          data?.message ?? "Resource not found.",
          status,
          "NOT_FOUND",
        );
        break;

      case 422: {
        const validationErrors =
          (data as { errors?: Record<string, string[]> })?.errors || {};
        apiError = new ApiError(
          data?.message ?? "Validation failed. Please check your input.",
          status,
          "VALIDATION_ERROR",
          validationErrors,
        );
        break;
      }

      case 429:
        apiError = new ApiError(
          "Too many requests. Please try again later.",
          status,
          "RATE_LIMIT",
        );
        break;

      case 500:
        apiError = new ApiError(
          "Internal server error. Please try again later.",
          status,
          "SERVER_ERROR",
        );
        break;

      default:
        apiError = new ApiError(
          (data as { message?: string })?.message ||
            "An unexpected error occurred.",
          status,
          "UNKNOWN_ERROR",
        );
    }

    if (!originalConfig?.skipErrorHandling) {
      toast.error(apiError.message);
    }

    throw apiError;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Public methods
  async request<T = unknown>(
    config: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.client.request<ApiResponse<T>>(config as any);
    return response.data;
  }

  async get<T = unknown>(
    url: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  async delete<T = unknown>(
    url: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }

  getTokenPayload(): JWTPayload | null {
    const token = getToken();
    if (!token) return null;
    return parseJWT(token);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
